import { ThrowLiteError } from '../lite-error.js';
import { F64, U8, F32 } from '../engine/typed-arrays.js';
import { shToPolynomial } from '../math/spherical-harmonics.js';

function parseRGBE(buffer) {
  const bytes = new U8(buffer);
  let pos = 0;
  function readLine() {
    let line = "";
    while (pos < bytes.length) {
      const ch = bytes[pos++];
      if (ch === 10) {
        break;
      }
      if (ch !== 13) {
        line += String.fromCharCode(ch);
      }
    }
    return line;
  }
  const sig = readLine();
  if (!sig.startsWith("#?")) {
    ThrowLiteError(142);
  }
  let format = "";
  while (pos < bytes.length) {
    const line = readLine();
    if (line === "") {
      break;
    }
    if (line.startsWith("FORMAT=")) {
      format = line.slice(7);
    }
  }
  if (format && format !== "32-bit_rle_rgbe") {
    ThrowLiteError(143, format);
  }
  const resLine = readLine();
  const resMatch = resLine.match(/-Y\s+(\d+)\s+\+X\s+(\d+)/);
  if (!resMatch) {
    ThrowLiteError(144, resLine);
  }
  const height = parseInt(resMatch[1], 10);
  const width = parseInt(resMatch[2], 10);
  const data = new F32(width * height * 3);
  const scanlineBuf = new U8(width * 4);
  for (let y = 0; y < height; y++) {
    pos = decodeScanline(bytes, pos, width, data, y * width * 3, scanlineBuf);
  }
  return { width, height, data };
}
function decodeScanline(bytes, pos, width, out, outOffset, scanline) {
  const isRle = width >= 8 && width <= 32767 && bytes[pos] === 2 && bytes[pos + 1] === 2 && (bytes[pos + 2] & 128) === 0;
  if (isRle) {
    if ((bytes[pos + 2] << 8) + bytes[pos + 3] !== width) {
      ThrowLiteError(145);
    }
    pos += 4;
    for (let ch = 0; ch < 4; ch++) {
      let ptr = ch;
      let count = 0;
      while (count < width) {
        if (pos >= bytes.length) {
          ThrowLiteError(146);
        }
        const a = bytes[pos++];
        if (a === 0) {
          ThrowLiteError(147);
        }
        if (a > 128) {
          const runLen = a - 128;
          if (runLen > width - count || pos >= bytes.length) {
            ThrowLiteError(148);
          }
          const val = bytes[pos++];
          for (let i = 0; i < runLen; i++) {
            scanline[ptr] = val;
            ptr += 4;
          }
          count += runLen;
        } else {
          if (a > width - count || pos + a > bytes.length) {
            ThrowLiteError(149);
          }
          for (let i = 0; i < a; i++) {
            scanline[ptr] = bytes[pos++];
            ptr += 4;
          }
          count += a;
        }
      }
    }
    for (let x = 0; x < width; x++) {
      rgbeToFloat(scanline[x * 4], scanline[x * 4 + 1], scanline[x * 4 + 2], scanline[x * 4 + 3], out, outOffset + x * 3);
    }
  } else {
    if (pos + width * 4 > bytes.length) {
      ThrowLiteError(150);
    }
    for (let x = 0; x < width; x++) {
      rgbeToFloat(bytes[pos], bytes[pos + 1], bytes[pos + 2], bytes[pos + 3], out, outOffset + x * 3);
      pos += 4;
    }
  }
  return pos;
}
function rgbeToFloat(r, g, b, e, out, off) {
  if (e === 0) {
    out[off] = out[off + 1] = out[off + 2] = 0;
  } else {
    const scale = Math.pow(2, e - 136);
    out[off] = r * scale;
    out[off + 1] = g * scale;
    out[off + 2] = b * scale;
  }
}
function computeSHFromEquirect(data, width, height) {
  const Y00 = 0.282094791773878;
  const Y1 = 0.48860251190292;
  const Y2_2c = 1.092548430592079;
  const Y20c = 0.31539156525252;
  const Y22c = 0.54627421529604;
  const sh = new F64(27);
  let totalWeight = 0;
  for (let py = 0; py < height; py++) {
    const phi = (py + 0.5) / height * Math.PI;
    const sinPhi = Math.sin(phi);
    const cosPhi = Math.cos(phi);
    const dOmega = sinPhi * (Math.PI / height) * (2 * Math.PI / width);
    for (let px = 0; px < width; px++) {
      const theta = (2 * (px + 0.5) / width - 1) * Math.PI;
      const x = sinPhi * Math.sin(theta);
      const y = cosPhi;
      const z = sinPhi * Math.cos(theta);
      const idx = (py * width + px) * 3;
      let cr = data[idx], cg = data[idx + 1], cb = data[idx + 2];
      const maxCh = Math.max(cr, cg, cb);
      if (maxCh > 4096) {
        const s = 4096 / maxCh;
        cr *= s;
        cg *= s;
        cb *= s;
      }
      const w = dOmega;
      totalWeight += w;
      const b0 = Y00;
      const b1 = Y1 * y, b2 = Y1 * z, b3 = Y1 * x;
      const b4 = Y2_2c * x * y, b5 = Y2_2c * y * z;
      const b6 = Y20c * (3 * z * z - 1);
      const b7 = Y2_2c * x * z, b8 = Y22c * (x * x - y * y);
      const basis = [b0, b1, b2, b3, b4, b5, b6, b7, b8];
      for (let i = 0; i < 9; i++) {
        const bw = basis[i] * w;
        sh[i] = sh[i] + cr * bw;
        sh[9 + i] = sh[9 + i] + cg * bw;
        sh[18 + i] = sh[18 + i] + cb * bw;
      }
    }
  }
  const correction = 4 * Math.PI / totalWeight;
  for (let i = 0; i < 27; i++) {
    sh[i] = sh[i] * correction;
  }
  const irradScale = [1, 2 / 3, 2 / 3, 2 / 3, 0.25, 0.25, 0.25, 0.25, 0.25];
  for (let ch = 0; ch < 3; ch++) {
    for (let i = 0; i < 9; i++) {
      sh[ch * 9 + i] = sh[ch * 9 + i] * irradScale[i];
    }
  }
  return shToPolynomial(sh);
}

export { computeSHFromEquirect, parseRGBE };
//# sourceMappingURL=hdr-parser.js.map
